import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, MapPin, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SEOPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await fetch(`${API_URL}/api/seo/public/${slug}`);
        if (res.ok) {
          setPage(await res.json());
        } else {
          setPage(null);
        }
      } catch (err) {
        console.error('Failed to fetch SEO page:', err);
        setPage(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="font-bold text-3xl text-white mb-4">Page Not Found</h1>
          <p className="text-zinc-400 mb-8">The page you're looking for doesn't exist.</p>
          <Link to="/" className="text-red-500 hover:text-red-400 flex items-center justify-center gap-2">
            <ArrowLeft size={16} /> Back Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const readTime = Math.max(2, Math.ceil((page.word_count || 800) / 250));

  return (
    <div className="min-h-screen bg-black" data-testid="seo-page-public">
      <Navbar />

      {/* Hero */}
      <div className="bg-gradient-to-b from-zinc-900 to-black pt-20 pb-12">
        {page.hero_image && (
          <div className="max-w-5xl mx-auto px-4 mb-8">
            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-red-900/20">
              <img src={page.hero_image} alt={page.title} className="w-full h-[300px] md:h-[400px] object-cover" data-testid="seo-hero-image" />
            </div>
          </div>
        )}
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-zinc-500 mb-4">
            <Link to="/" className="hover:text-red-400">Home</Link>
            <ChevronRight size={14} />
            {page.discipline && <><span className="hover:text-red-400">{page.discipline}</span><ChevronRight size={14} /></>}
            <span className="text-zinc-400">{page.location}</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-4" data-testid="seo-page-title">
            {page.title}
          </h1>

          {page.excerpt && (
            <p className="text-lg text-zinc-400 mb-4">{page.excerpt}</p>
          )}

          <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-500">
            {page.location && (
              <span className="flex items-center gap-1">
                <MapPin size={14} className="text-red-500" /> {page.location}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock size={14} /> {readTime} min read
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Article */}
          <article className="lg:col-span-2">
            <div className="prose prose-invert prose-lg max-w-none
              prose-headings:text-white prose-headings:font-bold
              prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-zinc-300 prose-p:leading-relaxed
              prose-li:text-zinc-300
              prose-a:text-red-500 prose-a:no-underline hover:prose-a:text-red-400
              prose-strong:text-white
              prose-hr:border-zinc-800"
              data-testid="seo-page-content"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{page.content}</ReactMarkdown>
            </div>

            {/* FAQ Schema Section */}
            {page.faq?.length > 0 && (
              <div className="mt-12 border-t border-zinc-800 pt-8" data-testid="seo-page-faq">
                <h2 className="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  {page.faq.map((item, i) => (
                    <details key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl group" open={i === 0}>
                      <summary className="px-5 py-4 cursor-pointer text-white font-medium flex items-center justify-between list-none">
                        {item.question}
                        <ChevronRight size={18} className="text-zinc-500 transition-transform group-open:rotate-90" />
                      </summary>
                      <p className="px-5 pb-4 text-zinc-400 text-sm leading-relaxed">{item.answer}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-1 space-y-6">
            {/* CTA Card */}
            <div className="bg-gradient-to-br from-red-600 to-red-800 rounded-xl p-6 text-center sticky top-24">
              <h3 className="font-bold text-xl text-white mb-2">Ready to Train?</h3>
              <p className="text-red-100 text-sm mb-4">
                Experience world-class training at Cobra Thai in {page.location || 'Pune'}.
              </p>
              <a
                href="https://cobra-thai.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-white text-red-600 font-bold rounded-lg hover:bg-zinc-100 transition-colors w-full"
                data-testid="seo-page-cta"
              >
                Book FREE Trial
              </a>
            </div>

            {/* Keywords */}
            {page.secondary_keywords?.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
                <h4 className="text-white font-semibold text-sm mb-3">Related Topics</h4>
                <div className="flex flex-wrap gap-2">
                  {page.secondary_keywords.map((kw, i) => (
                    <span key={i} className="text-xs bg-zinc-800 text-zinc-400 px-2 py-1 rounded">{kw}</span>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>

      {/* FAQ JSON-LD Schema */}
      {page.faq?.length > 0 && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": page.faq.map(item => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer,
              },
            })),
          }),
        }} />
      )}

      {/* Article Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": page.title,
          "description": page.meta_description,
          "author": { "@type": "Organization", "name": "Cobra Thai" },
          "publisher": { "@type": "Organization", "name": "Cobra Thai", "logo": { "@type": "ImageObject", "url": "https://www.cobra-thai.com/cobra-logo-circle.png" } },
          "datePublished": page.published_at || page.created_at,
          "dateModified": page.updated_at,
        }),
      }} />

      <Footer />
    </div>
  );
}
